import { useState, useRef } from 'react';
import { useIntersectionObserver } from '../../../hooks/useIntersectionObserver';

const FAQS = [
  {
    q: 'Who provides landscape design near me?',
    a: "Brainard's Greenscapes offers professional landscape design and outdoor construction for homeowners in Las Cruces, El Paso, Alamogordo, Ruidoso, and surrounding areas — delivering custom layouts, premium materials, and long-lasting results tailored to your property.",
  },
  {
    q: 'How much does landscaping cost?',
    a: 'Landscaping costs vary based on scope, materials, and design complexity. Simple enhancements may start at a few thousand dollars, while full outdoor transformations vary depending on your goals. We provide a transparent proposal after a no-obligation consultation.',
  },
  {
    q: 'How long does a landscaping project take?',
    a: "Smaller hardscape or lawn projects may be completed in a few days to a week. Full outdoor renovations — patios, pergolas, irrigation, and lighting — typically take 2–6 weeks. A clear schedule is provided during planning so you know exactly what to expect.",
  },
  {
    q: 'What types of outdoor living spaces do you build?',
    a: "We design and build stone patios, pergolas, outdoor kitchens, fire pits and fireplaces, water features, retaining walls, and complete entertainment areas — all custom-designed and tailored to your home and lifestyle.",
  },
  {
    q: 'Do you offer free consultations?',
    a: 'Yes — we offer a complimentary on-site consultation where we visit your property, assess your goals, and discuss all possibilities. No obligation, and you leave with a clear picture of what\'s possible for your space.',
  },
  {
    q: 'Are you licensed and insured?',
    a: "Absolutely. Brainard's Greenscapes is fully licensed and insured, giving you complete peace of mind. We carry all required contractor certifications for New Mexico and Texas landscaping work.",
  },
];

export default function FAQSection() {
  const ref = useRef<HTMLDivElement>(null);
  const visible = useIntersectionObserver(ref, { threshold: 0.1 });
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section className="bg-white py-28">
      <div className="max-w-7xl mx-auto px-8">
        <div
          ref={ref}
          className={`grid md:grid-cols-5 gap-16 items-start transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          {/* Left sticky header */}
          <div className="md:col-span-2 md:sticky md:top-24">
            <p className="font-sans text-forest-500 text-xs tracking-[0.25em] uppercase mb-4">Common Questions</p>
            <h2 className="font-serif text-charcoal-900 font-semibold leading-tight mb-6" style={{ fontSize: 'clamp(28px, 3vw, 44px)' }}>
              Frequently Asked<br />
              <em className="italic text-forest-500">Questions</em>
            </h2>
            <div className="w-10 h-0.5 bg-stone-400 mb-6" />
            <p className="font-sans text-charcoal-700 text-sm leading-relaxed mb-8">
              Have more questions? We're happy to walk you through everything during your free, no-obligation consultation.
            </p>
            <a
              href="tel:5756358883"
              className="inline-flex items-center gap-3 font-sans text-charcoal-900 text-sm font-medium cursor-pointer"
            >
              <div className="w-10 h-10 flex items-center justify-center bg-forest-50 rounded-full">
                <i className="ri-phone-fill text-forest-500 text-base" />
              </div>
              <div>
                <div className="text-[10px] text-charcoal-700 uppercase tracking-wider">Call Us Directly</div>
                <div className="font-semibold">(575) 635-8883</div>
              </div>
            </a>
          </div>

          {/* Right: Accordion */}
          <div className="md:col-span-3 space-y-3">
            {FAQS.map((faq, i) => (
              <FAQItem
                key={i}
                faq={faq}
                isOpen={openIndex === i}
                onToggle={() => setOpenIndex(openIndex === i ? null : i)}
                delay={i * 60}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function FAQItem({
  faq,
  isOpen,
  onToggle,
  delay,
}: {
  faq: typeof FAQS[0];
  isOpen: boolean;
  onToggle: () => void;
  delay: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const visible = useIntersectionObserver(ref, { threshold: 0.2 });

  return (
    <div
      ref={ref}
      className={`border border-stone-200 rounded-lg overflow-hidden transition-all duration-600 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'} ${isOpen ? 'border-forest-200' : ''}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left cursor-pointer hover:bg-stone-50 transition-colors"
        aria-expanded={isOpen}
      >
        <h4 className="font-serif text-charcoal-900 text-base font-medium">{faq.q}</h4>
        <div className={`flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full transition-all duration-300 ${isOpen ? 'bg-forest-500' : 'bg-stone-100'}`}>
          <i className={`ri-${isOpen ? 'subtract' : 'add'}-line text-${isOpen ? 'white' : 'charcoal-700'} text-sm`} />
        </div>
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-60 opacity-100' : 'max-h-0 opacity-0'}`}
      >
        <div className="px-6 pb-5 border-t border-stone-100">
          <p className="font-sans text-charcoal-700 text-sm leading-relaxed pt-4">{faq.a}</p>
        </div>
      </div>
    </div>
  );
}
